import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Save, MapPin } from "lucide-react";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Loader from "../../components/common/Loader";
import WatermarkSettingsModal from "../../components/common/WatermarkSettingsModal";
import EventImagePicker from "../../components/create-event/EventImagePicker";
import SavedLocationsModal from "../../components/create-event/SavedLocationsModal";
import InfoModal, { InfoButton } from "../../components/common/InfoModal";
import { ShareInfoContent, QRInfoContent, ChatAccessInfoContent, OpenDoorPhaseInfoContent } from "../../components/create-event/infoContent";
import {
  getEventCategories,
  createEvent,
  updateEvent,
  getELQRById,
  sendPrivateEventOTP,
  validateEventOTP,
} from "../../services/createEventService";
import { getEventById } from "../../services/eventService";
import { getWatermark } from "../../services/watermarkService";
import { normalizeSavedLocation } from "../../utils/eventLocation";
import { useAuth } from "../../context/AuthContext";
import { loadDraft, saveDraft, clearDraft, resolveDraftUserKey } from "../../utils/createEventDraft";
import { MAX_GIF_SIZE_BYTES, MAX_GIF_DURATION_SECONDS } from "../../utils/gifConstraints";

/**
 * Create / Edit Event screen, ported from the mobile app's
 * src/Screens/create-event-update/index.js — one screen for both, driven by
 * whether a route :id param is present, same as mobile's isEdit+eventId
 * route params (so there's one implementation to keep in sync, not two).
 *
 * Deliberate deviations from mobile, per the agreed scope:
 * - GIF: pick an existing .gif file (validated for size/duration), not
 *   mobile's in-app video-to-GIF recorder/converter (ConvertVideoToGif) —
 *   see EventImagePicker.jsx.
 * - Draft save is an explicit "Save Draft" button, not mobile's
 *   back-navigation confirmation alert (no hardware back button here).
 * - No "touched" gating on date/time fields — sensible defaults are shown
 *   and validated directly (avoids pointless friction on a desktop tool).
 * - OTP is skipped outside a production build (mirrors mobile's __DEV__ skip)
 *   — and, like mobile, skipped entirely in edit mode.
 */

const PUBLIC_CATEGORY_FALLBACK = [
  { label: "Concert", value: "Concert" },
  { label: "Theater", value: "Theater" },
  { label: "Music", value: "Music" },
  { label: "Culture", value: "Culture" },
  { label: "Party", value: "Party" },
  { label: "Software", value: "Software" },
  { label: "Other events", value: "Other events" },
];

const PRIVATE_CATEGORIES = [
  { label: "Birthday", value: "Birthday" },
  { label: "Wedding Party", value: "Wedding Party" },
  { label: "Private Party", value: "Private Party" },
  { label: "Company Party", value: "Company Party" },
  { label: "Others", value: "Others" },
];

const PRIVATE_DEFAULT_END_TIME_CATEGORIES = ["Birthday", "Wedding Party", "Private Party", "Company Party"];

const OPEN_DOOR_DAYS_OPTIONS = Array.from({ length: 9 }, (_, i) => String(i + 2)); // 2..10
const WHO_CAN_UPLOAD_OPTIONS = [
  { label: "Promoter only", value: "Promoter only" },
  { label: "Promoter and Users", value: "Promoter and Users" },
];

// English label -> translation key, used to localize the fallback/private
// category and who-can-upload option labels above at render time without
// touching their `value` (sent to the API / used for internal comparisons).
const CATEGORY_LABEL_KEYS = {
  Concert: "categories.concert",
  Theater: "categories.theater",
  Music: "categories.music",
  Culture: "categories.culture",
  Party: "categories.party",
  Software: "categories.software",
  "Other events": "categories.otherEvents",
  Birthday: "categories.birthday",
  "Wedding Party": "categories.weddingParty",
  "Private Party": "categories.privateParty",
  "Company Party": "categories.companyParty",
  Others: "categories.others",
};
const WHO_CAN_UPLOAD_LABEL_KEYS = {
  "Promoter only": "whoCanUploadOptions.promoterOnly",
  "Promoter and Users": "whoCanUploadOptions.promoterAndUsers",
};

const isDevBuild = process.env.NODE_ENV !== "production";
const OTP_RESEND_SECONDS = 60;

const pad2 = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const nowTimeStr = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const addMinutesToTimeStr = (timeStr, minutes) => {
  const [h, m] = timeStr.split(":").map(Number);
  const total = (h * 60 + m + minutes + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};
const combineDateTime = (dateStr, timeStr, dayOffset = 0) => {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  return new Date(y, mo - 1, d + dayOffset, h, mi);
};
const dateToInputStr = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const dateToTimeStr = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
const isSameCalendarDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Mirrors mobile's normalizePrivateEventCategory in create-event-update/index.js. */
const normalizePrivateEventCategory = (category) => {
  if (category === "Wedding") return "Wedding Party";
  if (category === "Corporate") return "Company Party";
  return category ?? "";
};

/** Fallback location object when an event has no saved LQRId — built straight from the event's own fields. */
const fallbackLocationFromEvent = (evt, t) => ({
  Id: null,
  LocationName: evt?.Location || t("createEvent.eventLocationFallback"),
  Latitude: Number(evt?.Latitude) || 0,
  Longitude: Number(evt?.Longitude) || 0,
  Radius: Number(evt?.Radius) || 20,
  Address: evt?.Location || "",
  City: evt?.City || "",
  Country: evt?.Country || "",
  LocationURL: evt?.LocationURL || null,
  IsGeofenceEnabled: false,
  GeofencePolygonJson: null,
  LocationCheckInPoints: [],
});

const CreateEvent = () => {
  const { t } = useTranslation(["events", "common"]);
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const isEdit = Boolean(eventId);
  const { user } = useAuth();
  const draftUserKey = resolveDraftUserKey(user);

  // --- edit-mode hydration ---
  const [loadingEvent, setLoadingEvent] = useState(isEdit);
  const [eventStarted, setEventStarted] = useState(false);

  // Restored once, synchronously, before first render — mirrors mobile's
  // loadDraft() on mount in create mode (create-event-update/index.js).
  // Using a lazy initializer (not an effect) means every field below,
  // including EventImagePicker's initialImages, is seeded correctly on the
  // very first render instead of racing a post-mount hydration.
  const [restoredDraft] = useState(() => (isEdit ? null : loadDraft(draftUserKey)));

  // --- basic fields ---
  const [name, setName] = useState(() => restoredDraft?.name || "");
  const [origin, setOrigin] = useState(() => restoredDraft?.origin || "Public"); // 'Public' | 'NonPublic'
  const [category, setCategory] = useState(() => restoredDraft?.category || "");
  const [publicCategories, setPublicCategories] = useState([]);

  // --- date/time ---
  const [eventDate, setEventDate] = useState(() => restoredDraft?.eventDate || todayStr());
  const [startTime, setStartTime] = useState(() => restoredDraft?.startTime || nowTimeStr());
  const [endTime, setEndTime] = useState(() => restoredDraft?.endTime || addMinutesToTimeStr(nowTimeStr(), 60));
  const [endTimeTouched, setEndTimeTouched] = useState(() => Boolean(restoredDraft?.endTimeTouched));
  const [nextDay, setNextDay] = useState(() => Boolean(restoredDraft?.nextDay));

  // --- location ---
  const [location, setLocation] = useState(() => restoredDraft?.location || null); // normalized saved location
  const [showLocationModal, setShowLocationModal] = useState(false);

  // --- other fields ---
  const [whoCanUpload, setWhoCanUpload] = useState(() => restoredDraft?.whoCanUpload || WHO_CAN_UPLOAD_OPTIONS[0].value);
  const [openDoorDays, setOpenDoorDays] = useState(() => restoredDraft?.openDoorDays || "5");
  const [description, setDescription] = useState(() => restoredDraft?.description || "");
  const [termsAgreed, setTermsAgreed] = useState(false); // never persisted, same as mobile's agreestate

  // --- watermark ---
  const [watermarkEnabled, setWatermarkEnabled] = useState(() => Boolean(restoredDraft?.watermarkEnabled));
  const [hasWatermark, setHasWatermark] = useState(null); // null = loading
  const [showWatermarkModal, setShowWatermarkModal] = useState(false);

  // --- images ---
  const imagePickerRef = useRef(null);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageCount, setImageCount] = useState(() => restoredDraft?.images?.length || 0);
  // hydrated once for edit mode or a restored draft, before EventImagePicker first mounts
  const [initialImages, setInitialImages] = useState(() => restoredDraft?.images || []);

  // --- OTP ---
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpResendIn, setOtpResendIn] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  // --- info modals ---
  const [activeInfo, setActiveInfo] = useState(null); // 'share' | 'qr' | 'chat' | 'openDoor' | null
  const INFO_MODALS = {
    share: { title: t("createEvent.infoModalTitles.whoCanUpload"), content: <ShareInfoContent /> },
    qr: { title: t("createEvent.infoModalTitles.eventQrCodes"), content: <QRInfoContent /> },
    chat: { title: t("createEvent.infoModalTitles.chatAccess"), content: <ChatAccessInfoContent /> },
    openDoor: { title: t("createEvent.infoModalTitles.openDoorPhase"), content: <OpenDoorPhaseInfoContent /> },
  };

  useEffect(() => {
    getEventCategories("Public")
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.Result || [];
        const mapped = raw
          .map((item) => {
            const label = item?.Label;
            const value = item?.Value ?? item?.Name ?? item?.Category ?? item?.Key ?? label;
            return label && value ? { label, value } : null;
          })
          .filter(Boolean);
        setPublicCategories(mapped.length ? mapped : PUBLIC_CATEGORY_FALLBACK);
      })
      .catch(() => setPublicCategories(PUBLIC_CATEGORY_FALLBACK));
  }, []);

  useEffect(() => {
    getWatermark()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.Result || [];
        setHasWatermark(list.length > 0);
      })
      .catch(() => setHasWatermark(false));
  }, []);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setInterval(() => setOtpResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpResendIn]);

  // Hydrate the form from the existing event, mirroring mobile's isEdit
  // useEffect in create-event-update/index.js. Everything else (validation,
  // payload assembly, submit) is the same code path as create — only the
  // starting values and a few disabled/skip rules differ.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await getEventById(eventId);
        const evt = res.data?.Result || res.data;
        if (cancelled || !evt) return;

        const resolvedOrigin =
          evt.Origin === "Public" || evt.Origin === "NonPublic" ? evt.Origin : "Public";
        const normalizedCategory = normalizePrivateEventCategory(evt.Category);
        const start = new Date(evt.StartDateTime);
        const end = new Date(evt.PromtorEventEndDateTime || evt.EndDateTime);
        const uploaderRole = evt.UploaderRole ? WHO_CAN_UPLOAD_OPTIONS[0].value : WHO_CAN_UPLOAD_OPTIONS[1].value;

        setOrigin(resolvedOrigin);
        setName(evt.Name || "");
        setCategory(normalizedCategory);
        setEventDate(dateToInputStr(start));
        setStartTime(dateToTimeStr(start));
        setEndTime(dateToTimeStr(end));
        setEndTimeTouched(true);
        setNextDay(!isSameCalendarDay(start, end));
        setWhoCanUpload(uploaderRole);
        setOpenDoorDays(String(evt.OpenDoorDays ?? "5"));
        setDescription(evt.Description || "");
        setWatermarkEnabled(Boolean(evt.IsWatermark));
        setEventStarted(start.getTime() < Date.now());

        if (evt.LQRId) {
          try {
            const locRes = await getELQRById(evt.LQRId);
            const rawLoc = locRes.data?.Result ?? locRes.data;
            if (!cancelled) setLocation(rawLoc ? normalizeSavedLocation(rawLoc) : fallbackLocationFromEvent(evt, t));
          } catch {
            if (!cancelled) setLocation(fallbackLocationFromEvent(evt, t));
          }
        } else {
          setLocation(fallbackLocationFromEvent(evt, t));
        }

        setInitialImages(
          Array.isArray(evt.EventImages)
            ? evt.EventImages.map((img) => ({
                url: img.Url ?? img.url,
                thumbnail: img.thumbnail ?? img.Thumbnail ?? img.Url ?? img.url,
              }))
            : []
        );
        setImageCount(Array.isArray(evt.EventImages) ? evt.EventImages.length : 0);
      } catch {
        toast.error(t("createEvent.toasts.loadEventFailed"));
      } finally {
        if (!cancelled) setLoadingEvent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, eventId]);

  const categoryOptions = origin === "Public" ? publicCategories : PRIVATE_CATEGORIES;
  const usesPrivateDefaultEndTime = origin !== "Public" && PRIVATE_DEFAULT_END_TIME_CATEGORIES.includes(category);

  const handleOriginChange = (value) => {
    if (isEdit) return; // mobile locks Origin once an event exists — same here
    setOrigin(value);
    setCategory("");
    setEndTimeTouched(false);
    setWhoCanUpload(value === "Public" ? WHO_CAN_UPLOAD_OPTIONS[0].value : WHO_CAN_UPLOAD_OPTIONS[1].value);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    if (origin !== "Public" && PRIVATE_DEFAULT_END_TIME_CATEGORIES.includes(value) && !endTimeTouched) {
      setEndTime("05:00");
      setNextDay(true);
    }
  };

  const handleStartTimeChange = (value) => {
    setStartTime(value);
    if (usesPrivateDefaultEndTime && !endTimeTouched) {
      setEndTime("05:00");
      setNextDay(true);
      return;
    }
    if (!endTimeTouched) {
      const crossesMidnight = Number(value.split(":")[0]) >= 23;
      setNextDay(crossesMidnight);
      setEndTime(addMinutesToTimeStr(value, 60));
    }
  };

  const shouldUploaderRoleBePromoterOnly = origin === "Public" || whoCanUpload === WHO_CAN_UPLOAD_OPTIONS[0].value;

  const buildPayload = (imageList) => {
    const startDateTime = combineDateTime(eventDate, startTime);
    const endDateTime = combineDateTime(eventDate, endTime, nextDay ? 1 : 0);
    const checkInPointCount = location?.LocationCheckInPoints?.length || 0;
    const hasZone = Boolean(location?.IsGeofenceEnabled && location?.GeofencePolygonJson);

    return {
      thumbnail: imageList[0]?.url,
      name,
      artist: "",
      eventType: category,
      startDateTime,
      endDateTime,
      Origin: origin,
      PromtorEventStartDateTime: startDateTime,
      PromtorEventEndDateTime: endDateTime,
      OpenDoorDays: openDoorDays,
      ApproxNoOfVisitors: "100",
      GalleryUsageFee: "0",
      IsOpenToAll: origin === "Public",
      location: location?.Address,
      latitude: location?.Latitude,
      longitude: location?.Longitude,
      City: location?.City,
      Country: location?.Country,
      ticketId: "0",
      ticketPrice: "0",
      category,
      description,
      eventImages: imageList,
      locationURL: location?.LocationURL ?? null,
      Radius: (location?.Radius ?? 20).toString(),
      UploaderRole: shouldUploaderRoleBePromoterOnly,
      QRCodeType: true,
      IsChatEnabled: true,
      MediaUploadMode: "2",
      IsWatermark: watermarkEnabled,
      QRType: "1",
      LQRId: location?.Id ?? null,
      UseMultipleCheckInPoints: checkInPointCount > 1,
      IsGeofenceEnabled: origin === "Public" && hasZone,
    };
  };

  const runSubmit = async (imageList) => {
    setSubmitting(true);
    try {
      const res = isEdit
        ? await updateEvent(eventId, buildPayload(imageList))
        : await createEvent(buildPayload(imageList));
      if (res.data?.Status === "Success" || res.data?.status === "Success") {
        toast.success(res.data?.Message || (isEdit ? t("createEvent.toasts.updated") : t("createEvent.toasts.created")));
        if (isEdit) {
          navigate(`/events/${eventId}`);
        } else {
          clearDraft(draftUserKey); // matches mobile's AsyncStorage.removeItem(DRAFT_KEY) on create success
          const newId = origin === "Private" ? res.data?.Result?.EventId : res.data?.Result;
          navigate(newId ? `/events/${newId}` : "/events");
        }
      } else {
        toast.error(res.data?.Message || (isEdit ? t("createEvent.toasts.updateFailed") : t("createEvent.toasts.createFailed")));
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.Result === "EventLimitReached") {
        toast.error(`${data?.Message || t("createEvent.toasts.eventLimitReachedFallback")} ${t("createEvent.toasts.requestMoreSuffix")}`);
      } else {
        toast.error(data?.Message || (isEdit ? t("createEvent.toasts.updateFailed") : t("createEvent.toasts.createFailed")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validate = () => {
    if (imagesLoading) return t("createEvent.validation.imagesUploading");
    if (!name.trim()) return t("createEvent.validation.nameRequired");
    if (!category) return t("createEvent.validation.categoryRequired");
    const startDateTime = combineDateTime(eventDate, startTime);
    const endDateTime = combineDateTime(eventDate, endTime, nextDay ? 1 : 0);
    if (endDateTime <= startDateTime) return t("createEvent.validation.endTimeAfterStart");
    if (!location?.Latitude || !location?.Longitude) return t("createEvent.validation.locationRequired");
    const days = parseInt(openDoorDays, 10);
    if (isNaN(days) || days < 2 || days > 10) return t("createEvent.validation.openDoorDaysRange");
    const mediaType = imagePickerRef.current?.getMediaType?.() || "images";
    if (mediaType === "gif") {
      if (imageCount !== 1) return t("createEvent.validation.gifExactlyOne");
      const gifMeta = imagePickerRef.current?.getSelectedGifMeta?.();
      if (gifMeta?.size > MAX_GIF_SIZE_BYTES) return t("createEvent.validation.gifMaxSize", { size: (MAX_GIF_SIZE_BYTES / (1024 * 1024)).toFixed(0) });
      if (gifMeta?.duration > MAX_GIF_DURATION_SECONDS) return t("createEvent.validation.gifMaxDuration", { seconds: MAX_GIF_DURATION_SECONDS });
    } else {
      if (imageCount < 1) return t("createEvent.validation.imageRequired");
      if (imageCount > 3) return t("createEvent.validation.imageMax");
    }
    if (!termsAgreed) return t("createEvent.validation.agreeTermsRequired");
    if (!isEdit) {
      const diffMinutes = (startDateTime.getTime() - Date.now()) / 60000;
      if (diffMinutes <= -2) return t("createEvent.validation.startTimeInPast");
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      toast.warning(error);
      return;
    }

    const imageList = imagePickerRef.current?.getImages() || [];

    // Mobile skips OTP for __DEV__ builds and for edit mode — same here.
    if (isDevBuild || isEdit) {
      await runSubmit(imageList);
      return;
    }

    // production create: OTP-gated
    if (!otpSent) {
      setSubmitting(true);
      try {
        await sendPrivateEventOTP();
        setOtpSent(true);
        setOtpResendIn(OTP_RESEND_SECONDS);
        toast.info(t("createEvent.toasts.otpSent"));
      } catch {
        toast.error(t("createEvent.toasts.otpSendFailed"));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!otpCode.trim()) {
      toast.warning(t("createEvent.validation.otpRequired"));
      return;
    }

    if (!otpVerified) {
      setSubmitting(true);
      try {
        await validateEventOTP(otpCode.trim());
        setOtpVerified(true);
        await runSubmit(imageList);
      } catch (err) {
        toast.error(err.response?.data?.Message || t("createEvent.toasts.otpInvalid"));
        setSubmitting(false);
      }
      return;
    }

    await runSubmit(imageList);
  };

  // Explicit "Save Draft" action — the web equivalent of mobile's saveDraft(),
  // which there is only reachable via the back-navigation confirmation alert.
  // A visible button is the fitting trigger for a desktop form since there's
  // no hardware back button / navigation-intercept dialog here.
  const handleSaveDraft = () => {
    const draftData = {
      name, origin, category, eventDate, startTime, endTime, endTimeTouched, nextDay,
      location, whoCanUpload, openDoorDays, description, watermarkEnabled,
      images: imagePickerRef.current?.getImages() || [],
    };
    const ok = saveDraft(draftUserKey, draftData);
    toast[ok ? "success" : "error"](ok ? t("createEvent.toasts.draftSaved") : t("createEvent.toasts.draftSaveFailed"));
  };

  const submitLabel = useMemo(() => {
    if (submitting) return t("common:actions.pleaseWait");
    if (isEdit) return t("createEvent.saveChangesButton");
    if (isDevBuild) return t("createEvent.createEventButton");
    if (!otpSent) return t("createEvent.sendCodeButton");
    if (!otpVerified) return t("createEvent.verifyAndCreateButton");
    return t("createEvent.createEventButton");
  }, [submitting, isEdit, otpSent, otpVerified, t]);

  if (isEdit && loadingEvent) return <Loader />;

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.heading}>{isEdit ? t("createEvent.editTitle") : t("createEvent.createTitle")}</h2>
        <div style={s.headerActions}>
          {!isEdit && (
            <Button variant="secondary" icon={Save} onClick={handleSaveDraft}>
              {t("createEvent.saveDraft")}
            </Button>
          )}
          <Button variant="secondary" onClick={() => navigate(isEdit ? `/events/${eventId}` : "/events")}>
            {t("common:actions.cancel")}
          </Button>
        </div>
      </div>

      <div style={s.card}>
        <span style={s.sectionLabel}>{t("createEvent.eventTypeLabel")}</span>
        <div style={s.radioRow}>
          {["Public", "NonPublic"].map((value) => (
            <label key={value} style={s.radioLabel}>
              <input
                type="radio"
                name="origin"
                checked={origin === value}
                disabled={isEdit && origin !== value}
                onChange={() => handleOriginChange(value)}
              />
              <span>{value === "Public" ? t("createEvent.eventType.public") : t("createEvent.eventType.nonPublic")}</span>
            </label>
          ))}
        </div>

        <Input label={t("createEvent.nameLabel")} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("createEvent.namePlaceholder")} />

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>{t("createEvent.categoryLabel")}</label>
          <select style={s.select} value={category} onChange={(e) => handleCategoryChange(e.target.value)}>
            <option value="">{t("createEvent.selectCategoryPlaceholder")}</option>
            {categoryOptions.map((c) => (
              <option key={c.value} value={c.value}>{CATEGORY_LABEL_KEYS[c.label] ? t(`createEvent.${CATEGORY_LABEL_KEYS[c.label]}`) : c.label}</option>
            ))}
          </select>
        </div>

        <div style={s.dateRow}>
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>{t("createEvent.dateLabel")}</label>
            <input
              type="date"
              style={s.select}
              value={eventDate}
              disabled={isEdit && eventStarted}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>{t("createEvent.startTimeLabel")}</label>
            <input
              type="time"
              style={s.select}
              value={startTime}
              disabled={isEdit && eventStarted}
              onChange={(e) => handleStartTimeChange(e.target.value)}
            />
          </div>
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>{t("createEvent.endTimeLabel")}</label>
            <input
              type="time"
              style={s.select}
              value={endTime}
              onChange={(e) => { setEndTime(e.target.value); setEndTimeTouched(true); }}
            />
          </div>
        </div>

        <label style={s.checkboxLabel}>
          <input type="checkbox" checked={nextDay} onChange={(e) => setNextDay(e.target.checked)} />
          <span>{t("createEvent.nextDayLabel")}</span>
        </label>

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>
            {t("createEvent.locationLabel")}
            <InfoButton label={t("createEvent.aboutQrInfoLabel")} onClick={() => setActiveInfo("qr")} />
          </label>
          <button type="button" style={s.locationBtn} onClick={() => setShowLocationModal(true)}>
            {location ? (
              <span>
                <strong>{location.LocationName}</strong>
                <span style={s.locationAddress}> — {location.Address}</span>
              </span>
            ) : (
              <span style={s.locationPlaceholder}><MapPin size={14} strokeWidth={2} style={{ verticalAlign: -2, marginRight: 5 }} />{t("createEvent.chooseLocationPlaceholder")}</span>
            )}
          </button>
        </div>

        {origin !== "Public" && (
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>
              {t("createEvent.whoCanUploadLabel")}
              <InfoButton label={t("createEvent.whoCanUploadInfoLabel")} onClick={() => setActiveInfo("share")} />
            </label>
            <select style={s.select} value={whoCanUpload} onChange={(e) => setWhoCanUpload(e.target.value)}>
              {WHO_CAN_UPLOAD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(`createEvent.${WHO_CAN_UPLOAD_LABEL_KEYS[o.label]}`)}</option>
              ))}
            </select>
          </div>
        )}

        <p style={s.chatAccessNote}>
          {t("createEvent.chatAccessLabel")} <strong>{t("createEvent.chatAccessTwoWay")}</strong> {t("createEvent.chatAccessDefaultSuffix")}
          <InfoButton label={t("createEvent.chatAccessInfoLabel")} onClick={() => setActiveInfo("chat")} />
        </p>

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>
            {t("createEvent.openDoorDaysLabel")}
            <InfoButton label={t("createEvent.openDoorInfoLabel")} onClick={() => setActiveInfo("openDoor")} />
          </label>
          <select style={s.select} value={openDoorDays} onChange={(e) => setOpenDoorDays(e.target.value)}>
            {OPEN_DOOR_DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        {origin === "Public" && (
          <div style={s.checkboxLabel}>
            <label style={s.checkboxLabelInline}>
              <input
                type="checkbox"
                checked={watermarkEnabled}
                onChange={() => {
                  if (hasWatermark) { setWatermarkEnabled((v) => !v); return; }
                  toast.info(t("createEvent.toasts.setupWatermarkFirst"));
                  setShowWatermarkModal(true);
                }}
              />
              <span>{t("createEvent.applyWatermarkLabel")}</span>
            </label>
            {hasWatermark === false && (
              <button type="button" style={s.linkBtn} onClick={() => setShowWatermarkModal(true)}>
                {t("createEvent.setupWatermarkButton")}
              </button>
            )}
          </div>
        )}

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>{t("createEvent.descriptionLabel")}</label>
          <textarea
            style={s.textarea}
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("createEvent.descriptionPlaceholder")}
          />
        </div>

        <EventImagePicker
          ref={imagePickerRef}
          initialImages={initialImages}
          onLoadingChange={setImagesLoading}
          onChange={(images) => setImageCount(images.length)}
        />

        <label style={s.checkboxLabel}>
          <input type="checkbox" checked={termsAgreed} onChange={(e) => setTermsAgreed(e.target.checked)} />
          <span>{t("createEvent.agreeTermsLabel")}</span>
        </label>

        {!isDevBuild && otpSent && (
          <div style={s.otpBox}>
            <label style={s.fieldLabel}>{t("createEvent.otpLabel")}</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              style={s.select}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              placeholder={t("createEvent.otpPlaceholder")}
            />
            <div style={s.otpHint}>
              {t("createEvent.otpHintPrefix")}{" "}
              {otpResendIn > 0 ? (
                <span>{t("createEvent.resendIn", { seconds: otpResendIn })}</span>
              ) : (
                <button
                  type="button"
                  style={s.linkBtn}
                  onClick={async () => {
                    try {
                      await sendPrivateEventOTP();
                      setOtpResendIn(OTP_RESEND_SECONDS);
                      toast.info(t("createEvent.toasts.codeResent"));
                    } catch {
                      toast.error(t("createEvent.toasts.resendFailed"));
                    }
                  }}
                >
                  {t("createEvent.resendButton")}
                </button>
              )}
            </div>
          </div>
        )}

        <div style={s.submitRow}>
          <Button onClick={handleSubmit} disabled={submitting || imagesLoading} style={s.submitBtn}>
            {submitLabel}
          </Button>
        </div>
      </div>

      {showLocationModal && (
        <SavedLocationsModal
          onClose={() => setShowLocationModal(false)}
          onConfirm={(loc) => setLocation(loc)}
        />
      )}

      {showWatermarkModal && (
        <WatermarkSettingsModal
          onClose={() => setShowWatermarkModal(false)}
          onSaved={() => setHasWatermark(true)}
        />
      )}

      {activeInfo && (
        <InfoModal title={INFO_MODALS[activeInfo].title} onClose={() => setActiveInfo(null)}>
          {INFO_MODALS[activeInfo].content}
        </InfoModal>
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  headerActions: { display: "flex", gap: 10 },
  heading: { margin: 0, fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  card: {
    background: "#fff", borderRadius: 14, padding: "24px 28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", maxWidth: 640,
  },
  sectionLabel: { display: "block", marginBottom: 8, fontWeight: 500, fontSize: 14, color: "#444" },
  radioRow: { display: "flex", gap: 20, marginBottom: 18 },
  radioLabel: { display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#444", cursor: "pointer" },
  fieldWrap: { marginBottom: 16 },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14, color: "#444" },
  select: {
    width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8,
    fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fafafa",
  },
  dateRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  checkboxLabel: {
    display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#444",
    cursor: "pointer", marginBottom: 16,
  },
  checkboxLabelInline: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer" },
  locationBtn: {
    width: "100%", textAlign: "left", padding: "12px 14px", border: "1.5px solid #e5e7eb",
    borderRadius: 8, background: "#fafafa", cursor: "pointer", fontSize: 14, color: "#1e1e2d",
  },
  locationAddress: { color: "#888", fontWeight: 400 },
  locationPlaceholder: { color: "#888" },
  chatAccessNote: { fontSize: 12.5, color: "#666", margin: "-8px 0 16px" },
  textarea: {
    width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: 8,
    fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fafafa", resize: "vertical",
    fontFamily: "inherit",
  },
  linkBtn: {
    background: "none", border: "none", color: "#6c63ff", fontWeight: 600,
    fontSize: 13, cursor: "pointer", marginLeft: 8, padding: 0,
  },
  otpBox: {
    background: "#f8f8fb", border: "1px solid #eee", borderRadius: 10, padding: "14px 16px", marginBottom: 16,
  },
  otpHint: { fontSize: 12, color: "#888", marginTop: 6 },
  submitRow: { marginTop: 8 },
  submitBtn: { width: "100%", padding: "12px 20px", fontSize: 15 },
};

export default CreateEvent;
